/* ============================================================================
   Formal stage submissions — the packaged close of a design stage. One click
   freezes every current sheet, mints a public package link, and hands the
   client the complete record to view, download or share. Listed here and in
   the client's portal thereafter.
   ========================================================================== */
import { useState } from "react";
import { Icon } from "./Icons";
import { Modal } from "./ui";
import { actions, STUDIO } from "../lib/store";
import { fmtDate } from "../lib/format";
import type { Project, StageKey } from "../lib/types";

export function StageSubmissionsPanel({ p, canEdit, by }: { p: Project; canEdit: boolean; by: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const subs = p.submissions ?? [];
  const sheetCount = (p.deliverables ?? []).filter((d) => !d.archived && (d.previewUrl || d.fileUrl)).length;

  const link = (token?: string) => `${location.origin}/pkg/${token}`;
  const copy = (token?: string, ref?: string) => {
    void navigator.clipboard?.writeText(link(token));
    setCopied(ref ?? null);
    setTimeout(() => setCopied(null), 1600);
  };

  return (
    <div className="card pad reveal" style={{ margin: "26px 0" }}>
      <div className="row between wrap" style={{ gap: 10, marginBottom: subs.length ? 14 : 4 }}>
        <div>
          <div className="kicker">Stage submissions</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4, maxWidth: 560 }}>
            Close a stage formally: every current sheet is frozen and submitted in full, and the client gets the complete package to view, download or share.
          </div>
        </div>
        {canEdit && (
          <button className="btn accent sm" onClick={() => setOpen(true)} disabled={sheetCount === 0}
            title={sheetCount === 0 ? "Upload drawings first — a submission packages the current set" : undefined}>
            <Icon.send width={14} />Submit a stage
          </button>
        )}
      </div>

      {subs.length > 0 && (
        <div className="col" style={{ gap: 8 }}>
          {subs.map((s) => (
            <div key={s.id} className="row between wrap panel" style={{ gap: 10, padding: "10px 14px" }}>
              <div style={{ minWidth: 0 }}>
                <div className="row wrap" style={{ gap: 8, alignItems: "baseline" }}>
                  <span className="badge accent mono" style={{ fontSize: 10 }}>{s.ref}</span>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{s.title}</span>
                </div>
                <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>
                  {s.sheets.length} sheets · submitted {fmtDate(s.submittedOn)} by {s.by}
                  {s.clientViewedOn ? ` · seen by the client ${fmtDate(s.clientViewedOn)}` : ""}
                </div>
                {s.note && <div className="dim" style={{ fontSize: 12, marginTop: 4, maxWidth: 560 }}>{s.note}</div>}
              </div>
              <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                <a className="btn ghost sm" href={link(s.packageToken)} target="_blank" rel="noreferrer"><Icon.arrowUpRight width={13} />Open</a>
                <button className="btn soft sm" onClick={() => copy(s.packageToken, s.ref)}>
                  {copied === s.ref ? <><Icon.check width={13} />Copied</> : <><Icon.link width={13} />Copy link</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && <SubmitModal p={p} by={by} onClose={() => setOpen(false)} />}
    </div>
  );
}

function SubmitModal({ p, by, onClose }: { p: Project; by: string; onClose: () => void }) {
  // default to the stage being worked — the active one, else the last done
  const activeIdx = p.stages.findIndex((s) => s.status === "active");
  const doneIdx = [...p.stages].reverse().find((s) => s.status === "done");
  const [stage, setStage] = useState<StageKey>((activeIdx >= 0 ? p.stages[activeIdx].key : doneIdx?.key) ?? p.stages[0].key);
  const stageLabel = p.stages.find((s) => s.key === stage)?.label ?? stage;
  const [title, setTitle] = useState(`${stageLabel} — Stage Submission`);
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);
  const sheetCount = (p.deliverables ?? []).filter((d) => !d.archived && (d.previewUrl || d.fileUrl)).length;

  return (
    <Modal open onClose={onClose} title="Submit a design stage">
      <div className="col" style={{ gap: 13 }}>
        <p className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
          All <b>{sheetCount} current sheets</b> are frozen as they stand and submitted in full. {p.client.name} is notified by email and gets the complete package in their portal — to view, download or forward.
        </p>
        <div className="field">
          <label>Stage</label>
          <select className="select" value={stage} onChange={(e) => {
            const k = e.target.value as StageKey;
            setStage(k);
            if (!touched) setTitle(`${p.stages.find((s) => s.key === k)?.label ?? k} — Stage Submission`);
          }}>
            {p.stages.map((s) => <option key={s.key} value={s.key}>{s.label}{s.status === "active" ? " — current" : s.status === "done" ? " — complete" : ""}</option>)}
          </select>
        </div>
        <div className="field"><label>Submission title</label>
          <input className="input" value={title} onChange={(e) => { setTitle(e.target.value); setTouched(true); }} /></div>
        <div className="field"><label>Covering note — what this stage concluded</label>
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="What was resolved at this stage, what the drawings show, and what happens next." /></div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!title.trim() || sheetCount === 0}
            onClick={() => { actions.submitStage(p.id, { stage, title: title.trim(), note: note.trim() || undefined }, by || STUDIO.principal); onClose(); }}>
            <Icon.send width={15} />Submit {sheetCount} sheets
          </button>
        </div>
      </div>
    </Modal>
  );
}
